import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import {
  collection, doc, query, where, orderBy, limit, onSnapshot, updateDoc, writeBatch,
  arrayUnion, arrayRemove, Timestamp, type Unsubscribe,
} from 'firebase/firestore'
import { getToken } from 'firebase/messaging'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { useNotification } from '@/composables/useNotification'

export type PushNotificationType = 'email_new' | 'email_associated' | 'email_mention'

export interface AppPushNotification {
  id: string
  type: PushNotificationType
  title: string
  body: string
  link: string
  emailId: string
  shipmentId?: string | null
  shipmentIdNumber?: string | null
  purchaseOrderId?: string | null
  purchaseOrder?: string | null
  read: boolean
  createdAt: Date
}

const MAX_NOTIFICATIONS = 30
const TOKEN_STORAGE_KEY = 'kilaFcmToken'

// Estado compartido: la campana del encabezado y el permiso de push viven una
// sola vez por pestaña aunque varios componentes usen el composable.
const notifications = ref<AppPushNotification[]>([])
const permission = ref<NotificationPermission | 'unsupported'>(
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
)
let unsubscribeFeed: Unsubscribe | null = null
let feedKey = ''
let isStarted = false
// Este dispositivo ya registró su token (persistido en localStorage).
const isPushEnabled = ref(false)
try { isPushEnabled.value = !!localStorage.getItem(TOKEN_STORAGE_KEY) } catch { /* sin storage */ }
// Motivo del último fallo al activar, para mostrárselo al usuario.
export type PushErrorReason = 'unsupported' | 'denied' | 'noVapid' | 'token'
const pushError = ref<{ reason: PushErrorReason; detail?: string } | null>(null)

/**
 * Notificaciones push del correo (KILA-362).
 *
 * Dos capas, para que funcione aunque falte alguna:
 * 1. Listener de Firestore sobre `Companies/{companyId}/notifications` del
 *    usuario: alimenta la campana y muestra el aviso en la app. No necesita
 *    permisos ni claves.
 * 2. Firebase Cloud Messaging: con permiso del navegador y la clave VAPID en
 *    `VITE_FIREBASE_VAPID_KEY`, el aviso llega aunque la pestaña esté cerrada.
 *    El token se guarda en `Users/{uid}.fcmTokens`, de donde lo lee la Cloud
 *    Function que envía.
 */
export function usePushNotifications() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const router = useRouter()
  const { showNotification } = useNotification()
  const db = FirebaseProvider.getInstance().getFirestore()

  const uid = computed(() => (authStore.user as { uid?: string } | null)?.uid || '')
  const companyId = computed(() => companyStore.selectedCompanyId || '')
  const unreadCount = computed(() => notifications.value.filter((n) => !n.read).length)
  const canEnablePush = computed(
    () => permission.value !== 'unsupported' && permission.value !== 'denied' && !!import.meta.env.VITE_FIREBASE_VAPID_KEY
  )

  function toNotification(id: string, data: any): AppPushNotification {
    const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date()
    return {
      id,
      type: data.type,
      title: data.title || '',
      body: data.body || '',
      link: data.link || '/dashboard/emails',
      emailId: data.emailId || '',
      shipmentId: data.shipmentId ?? null,
      shipmentIdNumber: data.shipmentIdNumber ?? null,
      purchaseOrderId: data.purchaseOrderId ?? null,
      purchaseOrder: data.purchaseOrder ?? null,
      read: !!data.read,
      createdAt,
    }
  }

  /** Suscribe la campana a los avisos del usuario en la compañía activa. */
  function startFeed() {
    const key = `${companyId.value}:${uid.value}`
    if (!companyId.value || !uid.value || key === feedKey) return
    stopFeed()
    feedKey = key

    const feedQuery = query(
      collection(db, 'Companies', companyId.value, 'notifications'),
      where('recipientUid', '==', uid.value),
      orderBy('createdAt', 'desc'),
      limit(MAX_NOTIFICATIONS)
    )

    let isFirstSnapshot = true
    unsubscribeFeed = onSnapshot(
      feedQuery,
      (snapshot) => {
        notifications.value = snapshot.docs.map((d) => toNotification(d.id, d.data()))
        // Solo los avisos que llegan estando la pestaña abierta se muestran como
        // toast; la carga inicial ya se ve en la campana.
        if (!isFirstSnapshot) {
          snapshot.docChanges()
            .filter((change) => change.type === 'added' && !change.doc.data().read)
            .forEach((change) => {
              const n = toNotification(change.doc.id, change.doc.data())
              showNotification('success', n.title, n.body)
            })
        }
        isFirstSnapshot = false
      },
      (err) => console.warn('[push] listener de notificaciones falló:', err)
    )
  }

  function stopFeed() {
    unsubscribeFeed?.()
    unsubscribeFeed = null
    feedKey = ''
  }

  /**
   * Pide permiso al navegador y registra el token FCM del dispositivo. Devuelve
   * false si el navegador no soporta push, el usuario lo negó o falta la clave.
   */
  async function enablePush(): Promise<boolean> {
    pushError.value = null
    const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY as string
    if (permission.value === 'unsupported') return fail('unsupported')
    if (!vapidKey) return fail('noVapid')
    if (!uid.value) return false

    if (permission.value !== 'granted') {
      permission.value = await Notification.requestPermission()
      if (permission.value !== 'granted') return fail('denied')
    }

    const messaging = await FirebaseProvider.getInstance().getMessaging()
    if (!messaging) return fail('unsupported')

    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration })
      if (!token) return false

      const previous = localStorage.getItem(TOKEN_STORAGE_KEY)
      const userRef = doc(db, 'Users', uid.value)
      if (previous && previous !== token) await updateDoc(userRef, { fcmTokens: arrayRemove(previous) })
      await updateDoc(userRef, { fcmTokens: arrayUnion(token) })
      localStorage.setItem(TOKEN_STORAGE_KEY, token)
      isPushEnabled.value = true
      return true
    } catch (err) {
      console.warn('[push] no se pudo registrar el token FCM:', err)
      return fail('token', err instanceof Error ? err.message : String(err))
    }
  }

  function fail(reason: PushErrorReason, detail?: string): false {
    pushError.value = { reason, detail }
    return false
  }

  /** Quita el token de este dispositivo (cerrar sesión). */
  async function disablePush(): Promise<void> {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY)
    if (token && uid.value) {
      await updateDoc(doc(db, 'Users', uid.value), { fcmTokens: arrayRemove(token) }).catch(() => {})
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY)
    isPushEnabled.value = false
  }

  async function markAsRead(notification: AppPushNotification) {
    if (notification.read || !companyId.value) return
    notification.read = true
    await updateDoc(doc(db, 'Companies', companyId.value, 'notifications', notification.id), { read: true })
  }

  async function markAllAsRead() {
    const unread = notifications.value.filter((n) => !n.read)
    if (!unread.length || !companyId.value) return
    const batch = writeBatch(db)
    unread.forEach((n) => {
      n.read = true
      batch.update(doc(db, 'Companies', companyId.value, 'notifications', n.id), { read: true })
    })
    await batch.commit()
  }

  async function open(notification: AppPushNotification) {
    await markAsRead(notification)
    router.push(notification.link)
  }

  /**
   * Arranque único por pestaña: sigue la sesión y la compañía activa, y si el
   * usuario ya había dado permiso, renueva el token sin volver a preguntar.
   */
  function start() {
    if (isStarted) return
    isStarted = true
    watch([uid, companyId], () => {
      if (uid.value && companyId.value) startFeed()
      else stopFeed()
    }, { immediate: true })
    watch(uid, (value) => {
      if (value && permission.value === 'granted') void enablePush()
    }, { immediate: true })
  }

  onBeforeUnmount(() => {
    // El estado es compartido: no se corta el feed al desmontar un consumidor.
  })

  return {
    notifications,
    unreadCount,
    permission,
    canEnablePush,
    isPushEnabled,
    pushError,
    start,
    enablePush,
    disablePush,
    markAsRead,
    markAllAsRead,
    open,
  }
}
